import { Component } from 'react';
import { Row, Col, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import applicationRouter from '~/hoc/applicationRouter';
import GenericList from '~/components/GenericList/GenericList';
import GenericFilter from '~/components/GenericList/GenericFilter.js';
import MemberInfoCard from '~/components/Card/MemberInfoCard.jsx';
import TimeBankAddList from './TimeBankAddList.js';
import TimeBankForm from './TimeBankForm.js';
import ImportFromExcelModal from '~/components/GenericModal/ImportFromExcelModal';

//elements
import Button from '../../../components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '../../../elements/LoadingSpinner.js';
import StatPill from '../../../elements/StatPill.js';
import StatSliderPill from '../../../elements/StatSliderPill.js';

//utils
import { shortDateFormat, totalTimeFormat } from '~/utils/dateFormat';
import { timeBankStatusMap, user_type_map } from '../../../utils/normalStatuses.js';
import { getIntegrationIconWithStatus } from '../../../utils/integrationUtils.js';
import { typeMap } from '../../../utils/constants.js'

//assets
import Edit from '../../../assets/img/editing.svg';
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import Delete from '../../../assets/img/deleting.svg';

class TimeBankList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            totalPayoutHours: 0,
            addModal: false,
            addId: null,
            selectedTimeBankIds: [],
            reloadList: new Date(),
            memberTotals: {},
            showPayoutOptionsModal: false,
            loading: false
        };
    }

    handleSetMemberTotals = () => {
        this.setState({
            memberTotals: this.props.timeBankStore?.currentMemberTotals
        });
    }

    onAdd = () => this.setState({ addModal: true });

    handleTableButtonAction = async (id, type, entity) => {
        const { handleDelete, timeBankStore } = this.props;
        if (type === 'edit') {
            this.setState({ addModal: true, addId: entity.timelog_id });
        }
        if (type === 'remove') {
            return handleDelete(id, async () => {
                const res = await timeBankStore.remove({ time_bank_id: id });
                if (res?.isOvertimeFlexitimeWithMultipleTimeBankLogs) {
                    this.handleDeleteTimeBankWithAutoFlexitime(id);
                }
                else {
                    timeBankStore.onFilter();
                    this.setState({ reloadList: new Date() });
                }
            });
        }
    };

    handleDeleteTimeBankWithAutoFlexitime = (id) => {
        const { t, commonStore, timeBankStore } = this.props;
        confirmAlert({
            title: t(`Are You Sure`),
            message: t('This time bank entry is registered by auto flexitime. This action will delete all the associated auto flexitime entry as well'),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: () => {
                        return timeBankStore.remove({ time_bank_id: id, deleteAll: true }).then((res2) => {
                            commonStore.addNotification(`${t(res2.message)}`, null, 'success');
                            timeBankStore.onFilter();
                            this.setState({ reloadList: new Date() });
                            return res2;
                        });
                    },
                },
                {
                    label: t('No'),
                    onClick: () => { }
                },
            ],
        });
    };

    onModalClose = () => {
        this.setState({ addModal: false, addId: null });
    };

    onPayout = (action) => {
        const { commonStore, timeBankStore, userDetails, t, router, reloadTimebankBalance } = this.props;
        const { selectedTimeBankIds } = this.state;

        const payload = {
            action,
            user_id: userDetails?.id,
            time_bank_ids: selectedTimeBankIds,
        };

        this.setState({ loading: true });

        const onSuccess = (res) => {
            commonStore.addNotification(t(res?.message), null, 'success');
            timeBankStore.onFilter();

            reloadTimebankBalance(userDetails.id)
            this.setState({
                reloadList: new Date(),
                showPayoutOptionsModal: false,
                totalPayoutHours: 0,
                loading: false,
            });

            return res;
        };

        if (action === 'takeAbsence') {
            return timeBankStore
                .saveHoursToCompOff(payload)
                .then((res) => {
                    return onSuccess(res);
                });
        }

        return timeBankStore.saveToIntegration(payload)
            .then((res) => {
                this.handleSetMemberTotals();
                return onSuccess(res);
            });
    };

    handleIntegration = (id, action) => {
        const { t, commonStore, timeBankStore, reloadTimebankBalance, userDetails } = this.props;
        const str = `${t('Please Note that Desyncing the time bank hours will remove the hours from Time&Control and integration product as well')}`;
        confirmAlert({
            title: t(`Confirm to ${action}`),
            message: str,
            buttons: [
                {
                    label: t('Yes'),
                    onClick: () => {
                        this.setState({ loading: true });
                        return timeBankStore.desyncIntegration({ id }).then((res2) => {
                            commonStore.addNotification(`${t(res2.message)} ${commonStore.config?.integration_details?.product}`, null, 'success');
                            timeBankStore.onFilter();
                            this.setState({ reloadList: new Date(), loading: false });
                            reloadTimebankBalance(userDetails.id)
                            return res2;
                        }).catch((err) => {
                            console.error(err);
                            this.setState({ loading: false });
                        });
                    },
                },
                {
                    label: t('No'),
                },
            ],
        });
    };

    handleStatusClick = async (timeBankRequestId, status, user_type) => {
        const { t, commonStore, timeBankStore, userDetails, reloadTimebankBalance } = this.props;
        if (user_type !== 'member') {
            if (status === 'active')
                return commonStore.addNotification(t('Please approve the work hours under the timesheet'), null, 'warning')
            if (status === 'comp_off') {
                confirmAlert({
                    title: t(`Are You Sure`),
                    message: t('Resetting the comp leave log will reset the current balance, and the same amount will be added back to the timebank balance'),
                    buttons: [
                        {
                            label: t('Yes'),
                            onClick: () => {
                                return timeBankStore.resetCompOffToTimeBank({ id: timeBankRequestId }).then((res2) => {
                                    commonStore.addNotification(`${t(res2.message)}`, null, 'success');
                                    timeBankStore.onFilter();
                                    this.setState({ reloadList: new Date() });
                                    reloadTimebankBalance(userDetails.id)
                                    return res2;
                                });
                            },
                        },
                        {
                            label: t('No'),
                        },
                    ],
                });
            }
        }
    }

    renderTableActionButtons = (id, entity) => {
        const { t, commonStore, userStore } = this.props;
        const isPM = userStore?.currentUser?.user_type === 'pm';
        const isMember = userStore?.currentUser?.user_type === 'member';
        const isRegisteredWithFlexitime = entity.type === 'normal'
        let statusText = entity?.status || 'active';
        statusText = timeBankStatusMap[statusText] || '';
        const disabledOnApprovedOrPaid = entity.status !== 'active';
        const isImportedApprovedAdmin = entity.type === 'imported' && entity.status === 'approved' && userStore.currentUser.user_type === 'admin';
        const isDeleteDisabled = (disabledOnApprovedOrPaid && !isImportedApprovedAdmin) || isPM || (!isImportedApprovedAdmin && isRegisteredWithFlexitime);

        return (
            <div className='actions-center actions-center__right'>
                {isMember ? null : (
                    <OverlayTrigger
                        key="desync_timebank"
                        placement="top"
                        overlay={
                            <Tooltip id="tooltip-top">
                                {t('Desync from integrations')}
                            </Tooltip>}
                    >
                        <div className='cursor-pointer' onClick={() => this.handleIntegration(id, 'Desync')}>
                            {getIntegrationIconWithStatus(commonStore?.config, entity, t, false)}
                        </div>
                    </OverlayTrigger>
                )}
                <div
                    className={`status-block status-block-height35 status-${entity.status || 'active'}`}
                    onClick={() => this.handleStatusClick(id, entity.status, userStore?.currentUser?.user_type)}
                >
                    <div className='text-capitalize'>
                        <div>{t(statusText)}</div>
                    </div>
                </div>
                <OverlayTrigger
                    key="edit_timebank"
                    placement="top"
                    overlay={
                        <Tooltip id="tooltip-top">{disabledOnApprovedOrPaid ?
                            t("Can't modify when status is Approved or Paid") :
                            isPM ?
                                t('Not Allowed')
                                :
                                isRegisteredWithFlexitime ?
                                    t("Can't modify logs which are registered by auto-flexitime") :
                                    `${t("Edit")} ${t("Request")}`}
                        </Tooltip>}
                >
                    <Button icon_sm fill onClick={() => this.handleTableButtonAction(id, 'edit', entity)} disabled={disabledOnApprovedOrPaid || isPM || isRegisteredWithFlexitime}>
                        <img src={Edit} alt="edit" />
                    </Button>
                </OverlayTrigger>
                <OverlayTrigger
                    key="Delete_request"
                    placement="top"
                    overlay={
                        <Tooltip id="tooltip-top">{isDeleteDisabled && disabledOnApprovedOrPaid ?
                            t("Deletion is not allowed when status is Approved or Paid") :
                            isPM ?
                                t('Not Allowed')
                                :
                                isRegisteredWithFlexitime ?
                                    t("Deletion is not allowed for logs which are registered by auto-flexitime") :
                                    `${t("Delete")} ${t("Request")}`}
                        </Tooltip>}
                >
                    <Button icon_sm_delete fill onClick={() => this.handleTableButtonAction(id, 'remove')} disabled={isDeleteDisabled}>
                        <img src={Delete} alt="delete" />
                    </Button>
                </OverlayTrigger>
            </div>
        );
    };

    onSelectionChanged = (ids) => {
        const { timeBankStore } = this.props;
        const currentList = timeBankStore.currentMemberTimeBankList;

        const totalHours = currentList.reduce((acc, curr) => {
            return ids[curr?.id] ? acc + curr.requested_hours : acc;
        }, 0);

        this.setState({
            selectedTimeBankIds: Object.keys(ids).filter(key => ids[key] && Number(key)),
            totalPayoutHours: totalHours
        });
    };

    getRowClassName = (row) => {
        return row.paid_by_timelog ? 'already-paid-bg-color' : ''
    }

    render() {
        const { t, timeBankStore, userDetails, userStore, commonStore } = this.props;
        const { totalPayoutHours, addModal, addId, showPayoutOptionsModal, selectedTimeBankIds, loading } = this.state;
        const { filters, appliedFilters, setFilter, onFilter } = timeBankStore;
        if (timeBankStore.loading || loading) return <LoadingSpinner />;

        const isMember = userStore?.currentUser?.user_type === 'member';
        const isPM = userStore?.currentUser?.user_type === 'pm';
        const isAdmin = userStore?.currentUser?.user_type === 'admin';
        const clientData = commonStore?.config?.client?.data;
        const dateTimeRules = commonStore.getDateTimeRules();;
        const timeBankSettings = clientData?.basicRules?.timeBankSettings || false;
        const allowImportFromExcel = clientData?.basicRules?.allowImportFromExcel;
        const maxLimit = Number(timeBankSettings.maxHourLimitInCycle * 60) + Number(timeBankSettings.maxMinuteLimitInCycle || 0);
        const isNegativeTimebank = (userDetails?.total_hours_saved || 0) < 0;
        const timebankUsagePercentage = maxLimit ? (Math.abs(userDetails?.total_hours_saved || 0) / maxLimit) * 100 : 0;
        const integrationProduct = commonStore.config?.integration_details?.product || '';
        const currentMemberTimeBankList = timeBankStore.currentMemberTimeBankList && timeBankStore.currentMemberTimeBankList.length ? timeBankStore.currentMemberTimeBankList : [];
        const importedIds = currentMemberTimeBankList.filter(ele => ele.type === 'imported')?.map(ele => ele.id)
        const onlyImportedIdSelected = selectedTimeBankIds.every(id => importedIds.includes(Number(id)));
        const memberInfoLines = [
            `${t('Emp. Id.')}: ${userDetails?.internal_number || ''}`,
            userDetails?.user_type ? t(user_type_map[userDetails.user_type] || userDetails.user_type) : '',
        ];

        return (
            <div className='mt-2 time_bank_list'>
                <Row>
                    {isMember ?
                        <Col md={2} className='d-flex justify-content-end'></Col>
                        :
                        <Col md={2} className='d-flex align-items-center'>
                            <MemberInfoCard
                                imageId={userDetails?.image ? userDetails?.image : false}
                                name={userDetails?.name}
                                lines={memberInfoLines}
                            />
                        </Col>
                    }
                    <Col md={10} className='d-flex justify-content-end align-items-center flex-wrap gap-2'>
                        {!isMember &&
                            <StatSliderPill
                                label={t('Timebank')}
                                value={`${totalTimeFormat(userDetails?.total_hours_saved || 0, dateTimeRules)} / ${totalTimeFormat(maxLimit, dateTimeRules)}`}
                                percentage={timebankUsagePercentage}
                                negative={isNegativeTimebank}
                                tooltip={`${isNegativeTimebank ? '-' : ''}${Math.round(timebankUsagePercentage)}% ${t('of timebank limit has been used out of max limit of')} ${totalTimeFormat(maxLimit, dateTimeRules)} ${t('hours')}`}
                            />
                        }
                        <OverlayTrigger
                            key="comp_balance"
                            placement="left"
                            overlay={
                                <Tooltip id="tooltip-bottom">
                                    {t('The remaining comp-off balance is displayed based on total calculated hours, not normal (standard) hours')}
                                </Tooltip>}
                        >
                            <StatPill items={[{
                                label: t('Comp Off'),
                                value: totalTimeFormat((userDetails?.total_comp_off_hours_saved - userDetails?.total_comp_off_hours_used) || 0, dateTimeRules)
                            }]} />
                        </OverlayTrigger>
                        {isMember &&
                            <Button fill wd onClick={this.onAdd}>
                                <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Flexitime')}
                            </Button>
                        }
                    </Col>
                </Row>
                <GenericList
                    columns={[
                        {
                            Header: 'Timesheet Date',
                            id: 'timelog_date',
                            accessor: ({ timelog_date }) => <span>{shortDateFormat(timelog_date, dateTimeRules)}</span>,
                            maxWidth: 60,
                            minWidth: 60
                        },
                        {
                            Header: 'Hours',
                            id: 'requested_hours',
                            accessor: ({ requested_hours }) => <span>{totalTimeFormat(requested_hours, dateTimeRules)}</span>,
                            maxWidth: 70,
                            minWidth: 70
                        },
                        {
                            Header: 'Ratio',
                            id: 'ratio',
                            accessor: ({ ratio }) => <span>{ratio}</span>,
                            maxWidth: 50,
                            minWidth: 50
                        },
                        {
                            Header: 'Calculated Hours',
                            id: 'adjusted_hours',
                            accessor: ({ adjusted_hours, requested_hours, ratio }) => (
                                <div>
                                    <span className='fw-600'>{totalTimeFormat(adjusted_hours, dateTimeRules)}</span>
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-top">
                                                <div>{t('Total normal hours')} - {totalTimeFormat(requested_hours, dateTimeRules)}</div>
                                                {ratio > 1 && (
                                                    <>
                                                        <div>{t('Overtime percent')} - {ratio * 100 - 100}%</div>
                                                        <div>{t('Total calculated hours')} - {totalTimeFormat(adjusted_hours, dateTimeRules)}</div>
                                                        <div>**{t('Total calculated hours is calculated on the basis of normal hours and addition of overtime percentage')}</div>
                                                    </>
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-info-circle text-muted" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </div>
                            ),
                            maxWidth: 70,
                            minWidth: 70
                        },
                        {
                            Header: 'Type',
                            id: 'type',
                            accessor: ({ type, is_imported }) => <span className='text-capitalize'>{is_imported ? t(typeMap['imported']) : t(typeMap[type])}</span>,
                            maxWidth: 70,
                            minWidth: 70
                        },
                        {
                            Header: 'Category type',
                            id: 'moved_type',
                            accessor: ({ hours_moved_type }) => <span>{hours_moved_type ?? '-'}</span>,
                            maxWidth: 70,
                            minWidth: 70
                        },
                        {
                            Header: 'Actions',
                            id: 'actions',
                            accessor: (entity) => this.renderTableActionButtons(entity.id, entity),
                        },
                    ]}
                    header={
                        <GenericFilter
                            filters={filters}
                            setFilter={(name, value) => this.props.timeBankStore.setFilter(name, value)}
                            onFilter={() => this.props.timeBankStore.onFilter()}
                            loading={loading}
                            hasSearch={false}
                            hasDateFilters={true}
                        />
                    }
                    getRowClassName={this.getRowClassName}
                    filters={appliedFilters}
                    allowCheckboxes={!isMember}
                    onSelectionChanged={this.onSelectionChanged}
                    requestData={(params) => timeBankStore.loadTimeBankList(params, () => this.handleSetMemberTotals())}
                    showCheckBoxOnApproved
                >
                    {!isMember &&
                        <>
                            {isAdmin && allowImportFromExcel &&
                                <ImportFromExcelModal
                                    importEntityType={'timeBankBalance'}
                                    entityName={`${t('Time Bank')} ${t('Balance')}`}
                                    sampleFileName={'used_timebank_balance_import_sample_file.xlsx'}
                                />
                            }
                            <Button fill wd onClick={() => this.setState({ showPayoutOptionsModal: true })} disabled={!totalPayoutHours || (timeBankSettings?.allowAutomaticFlexitimeTimeBank && !timeBankSettings.allowManualSyncingForAutomaticFlexitime)}>
                                {t('Pay Out')} {totalPayoutHours && totalTimeFormat(totalPayoutHours, dateTimeRules)}{t('hr')}
                            </Button>
                            <Button fill wd onClick={this.onAdd} disabled={(!isMember || (isPM && userStore?.currentUser?.id !== userDetails.id))}>
                                <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Flexitime')}
                            </Button>
                        </>
                    }
                </GenericList>
                <Modal
                    className='modal right fade modal_revised'
                    size="lg"
                    show={addModal}
                    onHide={this.onModalClose}
                >
                    <Modal.Header className="set_ModalHeader">
                        <Modal.Title className='w-100 text-center custom-modal-title'>
                            {t('Available hours that can be saved in the time bank')}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            onClick={this.onModalClose}
                            alt='close-btn'
                        />
                    </Modal.Header>
                    <Modal.Body>
                        {addId ? (
                            <TimeBankForm
                                timelog_id={addId}
                                goBack={() => this.setState({ addId: null })}
                                onClose={this.onModalClose}
                                userDetails={userDetails}
                            />
                        ) : (
                            <TimeBankAddList addTimelogId={(id) => this.setState({ addId: id })} />
                        )}
                    </Modal.Body>
                </Modal>
                <Modal
                    size="md"
                    centered
                    show={showPayoutOptionsModal}
                    onHide={() => this.setState({ showPayoutOptionsModal: false })}
                >
                    <Modal.Header className="set_ModalHeader">
                        <Modal.Title className='w-100 text-center custom-modal-title'>
                            {t('Select an option')}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            onClick={() => this.setState({ showPayoutOptionsModal: false })}
                            alt='close-btn'
                        />
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <div>
                                {(timeBankSettings?.sendFullHoursToPayroll) && (
                                    <div className=''>
                                        <b className='fs14'>{t('Notes')}</b>
                                        <ul className='fs12'>
                                            <li><i>{t('Timebank has enabled to send all the hours to payroll from timesheet prior to deduction')}.</i></li>
                                            <li><i>{t('These hours are already synced from timesheet, so it is advised to use these hours as comp-off')}!</i></li>
                                            <li><i>{t('When using payout, The time bank requests can be paid twice')}!</i></li>
                                        </ul>
                                    </div>
                                )}
                                {/* {(timeBankSettings?.allowAutomaticFlexitimeTimeBank) && (<i>{t('Flexible Option is disabled as overtime is only eligible for time off or payout')}!</i>)} */}
                            </div>
                        </Row>
                        <Row className="mt-2 pb-2">
                            <div className='d-flex justify-content-around'>
                                <Button variant="secondary" onClick={() => this.onPayout('payout')} className="btn-wd btn-fill" disabled={onlyImportedIdSelected || !integrationProduct}>
                                    {t('Pay out')}
                                </Button>
                                <Button variant="secondary" onClick={() => this.onPayout('takeAbsence')} className="btn-wd btn-fill">
                                    {t('Take comp leave')}
                                </Button>
                                <Button variant="secondary" onClick={() => this.onPayout('flexitime')} className="btn-wd btn-fill" disabled={onlyImportedIdSelected || !integrationProduct || (timeBankSettings?.allowAutomaticFlexitimeTimeBank && !userDetails?.allow_auto_flexitime)}>
                                    {t('Flexitime')}
                                </Button>
                            </div>
                        </Row>
                    </Modal.Body>
                </Modal>
            </div >
        );
    }
}

export default inject('commonStore', 'userStore', 'timeBankStore')(applicationRouter(withLocalization(withRemoveDialog(observer(TimeBankList)))));